<template>
	<div v-if="visible" class="tour-root" tabindex="0" @keydown.esc.prevent="end">
		<div v-if="targetRect">
			<div class="mask-top" :style="maskTopStyle" @mousedown.stop.prevent @mouseup.stop.prevent @click.stop.prevent @pointerdown.stop.prevent></div>
			<div class="mask-left" :style="maskLeftStyle" @mousedown.stop.prevent @mouseup.stop.prevent @click.stop.prevent @pointerdown.stop.prevent></div>
			<div class="mask-right" :style="maskRightStyle" @mousedown.stop.prevent @mouseup.stop.prevent @click.stop.prevent @pointerdown.stop.prevent></div>
			<div class="mask-bottom" :style="maskBottomStyle" @mousedown.stop.prevent @mouseup.stop.prevent @click.stop.prevent @pointerdown.stop.prevent></div>
		</div>
		<div v-else class="tour-mask" :style="{ pointerEvents: currentStep?.showScrollArrow ? 'none' : 'auto' }" @mousedown.stop.prevent @mouseup.stop.prevent @click.stop.prevent @pointerdown.stop.prevent></div>

		<div v-if="currentStep" class="tour-highlight" :style="highlightStyle" @mousedown.stop.prevent @mouseup.stop.prevent @click.stop.prevent @pointerdown.stop.prevent></div>

		<!-- Animated connection-arrow overlay -->
		<div v-if="showConnectAnimation" class="tour-connect-overlay">
			<svg class="tour-connect-svg" viewBox="0 0 800 200" preserveAspectRatio="xMidYMid meet">
				<!-- Node 1: Webhook Trigger -->
				<rect x="40" y="70" width="150" height="60" rx="12" class="tour-node-box" />
				<text x="115" y="105" text-anchor="middle" class="tour-node-label">Webhook Trigger</text>

				<!-- Arrow 1: Webhook → Send Email -->
				<line x1="190" y1="100" x2="300" y2="100" class="tour-arrow-line" />
				<polygon points="295,90 315,100 295,110" class="tour-arrow-head" />
				<!-- Animated dot 1 -->
				<circle r="6" class="tour-arrow-dot">
					<animateMotion dur="1.4s" repeatCount="indefinite" path="M190,100 L300,100" />
				</circle>

				<!-- Node 2: Send Email -->
				<rect x="320" y="70" width="150" height="60" rx="12" class="tour-node-box" />
				<text x="395" y="105" text-anchor="middle" class="tour-node-label">Send Email</text>

				<!-- Arrow 2: Send Email → Respond to Webhook -->
				<line x1="470" y1="100" x2="580" y2="100" class="tour-arrow-line" />
				<polygon points="575,90 595,100 575,110" class="tour-arrow-head" />
				<!-- Animated dot 2 -->
				<circle r="6" class="tour-arrow-dot tour-arrow-dot--delayed">
					<animateMotion dur="1.4s" repeatCount="indefinite" path="M470,100 L580,100" />
				</circle>

				<!-- Node 3: Respond to Webhook -->
				<rect x="600" y="70" width="160" height="60" rx="12" class="tour-node-box" />
				<text x="680" y="105" text-anchor="middle" class="tour-node-label">Respond to Webhook</text>
			</svg>
			<div class="tour-connect-hint">
				<span class="tour-connect-hint__icon">&#x2192;</span>
				Hover on a node's arrow icon, then drag to the next node to connect them
			</div>
		</div>

		<!-- Drag-hint overlay for integration rows -->
		<div v-if="showDragHint && targetRect" class="tour-drag-hint-overlay" :style="dragHintStyle">
			<div class="tour-drag-hint-container">
				<div class="tour-drag-hint-arrow">
					<svg width="120" height="40" viewBox="0 0 120 40">
						<line x1="10" y1="20" x2="90" y2="20" stroke="#3b82f6" stroke-width="3" stroke-dasharray="6 4" class="tour-drag-line" />
						<polygon points="85,10 105,20 85,30" fill="#3b82f6" class="tour-drag-arrow-head" />
						<circle r="5" fill="#60a5fa" class="tour-drag-dot">
							<animateMotion dur="1s" repeatCount="indefinite" path="M10,20 L90,20" />
						</circle>
					</svg>
				</div>
				<div class="tour-drag-hint-label">Drag to canvas →</div>
			</div>
		</div>

		<!-- Scroll suggestion overlay -->
		<div v-if="currentStep?.showScrollArrow" class="tour-scroll-suggestion" :class="`is-${currentStep.showScrollArrow}`" :style="scrollSuggestionStyle">
			<div class="tour-scroll-suggestion__content">
				<div class="tour-scroll-suggestion__arrow">
					<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
						<path v-if="currentStep.showScrollArrow === 'down'" d="M7 13l5 5 5-5M7 6l5 5 5-5" />
						<path v-else d="M17 11l-5-5-5 5M17 18l-5-5-5 5" />
					</svg>
				</div>
				<div class="tour-scroll-suggestion__text">Scroll {{ currentStep.showScrollArrow }} to continue</div>
			</div>
		</div>

		<div
			v-if="currentStep"
			class="tour-card"
			:class="{ 'is-dragging': dragging }"
			:style="cardStyle"
			role="dialog"
			aria-modal="true"
			@mousedown="onDragStart"
			@touchstart.stop.prevent="onDragStart"
		>
			<div class="tour-card__header">
				<div class="tour-card__title">{{ currentStep.title }}</div>
				<div class="tour-card__drag-hint">Drag</div>
				<button class="tour-card__close" type="button" @click="end" aria-label="End tour">×</button>
			</div>

			<div class="tour-card__body">
				<div class="tour-card__content" v-html="renderedStepContent"></div>
				<div v-if="currentStep.codeSnippet" class="tour-card__snippet">
					<pre>{{ currentStep.codeSnippet }}</pre>
					<div class="tour-card__snippet-actions">
						<el-button size="mini" type="primary" plain @click="copySnippet">Copy JSON</el-button>
					</div>
				</div>
			</div>

			<div class="tour-card__footer">
				<div class="tour-card__progress">Step {{ stepIndex + 1 }} / {{ steps.length }}</div>
				<div class="tour-card__actions">
					<el-button v-if="stepIndex > 0" size="mini" plain @click="prev">Back</el-button>
					<el-button v-if="showManualNext" size="mini" type="primary" :disabled="!canManualNext" @click="advance">Next</el-button>
					<el-button size="mini" type="danger" plain @click="end">End Tour</el-button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "WorkflowTour",
	props: {
		visible: { type: Boolean, default: false },
		steps: { type: Array, default: () => [] },
		initialStepIndex: { type: Number, default: 0 }
	},
	data() {
		return {
			stepIndex: 0,
			targetRect: null,
			modalRect: null,
			recalcTimer: null,
			autoAdvanceTimer: null,
			advanceInProgress: false,
			manualCardPosition: null,
			dragging: false,
			dragOffsetX: 0,
			dragOffsetY: 0,
			inputChangeTick: 0,
			missingTargetTicks: 0
		};
	},
	computed: {
		currentStep() {
			return this.steps && this.steps.length ? this.steps[this.stepIndex] : null;
		},
		showManualNext() {
			// Show Next button whenever manualAdvance is explicitly true
			return this.currentStep?.manualAdvance === true;
		},
		showConnectAnimation() {
			return this.visible && this.currentStep?.showConnectAnimation === true;
		},
		showDragHint() {
			return this.visible && this.currentStep?.showDragHint === true;
		},
		renderedStepContent() {
			const content = String(this.currentStep?.content || "");
			return content
				.replace(/senderEmail/g, "fromEmail")
				.replace(/receiverEmail/g, "toEmail");
		},
		canManualNext() {
			const _tick = this.inputChangeTick;
			if (_tick < 0) return false;
			// For manual steps, let user control progression.
			if (this.showManualNext) return true;
			return this.ruleMatches(this.getAdvanceRule());
		},
		highlightStyle() {
			if (!this.targetRect) return { display: "none" };
			const pad = 8;
			return {
				left: `${Math.max(0, this.targetRect.left - pad)}px`,
				top: `${Math.max(0, this.targetRect.top - pad)}px`,
				width: `${Math.max(0, this.targetRect.width + pad * 2)}px`,
				height: `${Math.max(0, this.targetRect.height + pad * 2)}px`
			};
		},
		maskTopStyle() {
			if (!this.targetRect) return { display: "none" };
			const pad = 4;
			const holeTop = Math.max(0, this.targetRect.top - pad);
			const isLowProfile = this.currentStep?.lowProfileOverlay || this.currentStep?.showDragHint || this.currentStep?.showScrollArrow;
			return { 
				left: "0px", 
				top: "0px", 
				width: "100vw", 
				height: `${holeTop}px`,
				pointerEvents: isLowProfile ? "none" : "auto",
				opacity: isLowProfile ? 0.05 : 1
			};
		},
		maskLeftStyle() {
			if (!this.targetRect) return { display: "none" };
			const pad = 4;
			const holeTop = Math.max(0, this.targetRect.top - pad);
			const holeLeft = Math.max(0, this.targetRect.left - pad);
			const holeHeight = Math.max(0, this.targetRect.height + pad * 2);
			const isLowProfile = this.currentStep?.lowProfileOverlay || this.currentStep?.showDragHint || this.currentStep?.showScrollArrow;
			return { 
				left: "0px", 
				top: `${holeTop}px`, 
				width: `${holeLeft}px`, 
				height: `${holeHeight}px`,
				pointerEvents: isLowProfile ? "none" : "auto",
				opacity: isLowProfile ? 0.05 : 1
			};
		},
		maskRightStyle() {
			if (!this.targetRect) return { display: "none" };
			const pad = 4;
			const holeTop = Math.max(0, this.targetRect.top - pad);
			const holeRight = Math.min(window.innerWidth || 0, this.targetRect.right + pad);
			const holeHeight = Math.max(0, this.targetRect.height + pad * 2);
			const isLowProfile = this.currentStep?.lowProfileOverlay || this.currentStep?.showDragHint || this.currentStep?.showScrollArrow;
			return {
				left: `${holeRight}px`,
				top: `${holeTop}px`,
				width: `${Math.max(0, (window.innerWidth || 0) - holeRight)}px`,
				height: `${holeHeight}px`,
				pointerEvents: isLowProfile ? "none" : "auto",
				opacity: isLowProfile ? 0.05 : 1
			};
		},
		maskBottomStyle() {
			if (!this.targetRect) return { display: "none" };
			const pad = 4;
			const holeBottom = Math.min(window.innerHeight || 0, this.targetRect.bottom + pad);
			const isLowProfile = this.currentStep?.lowProfileOverlay || this.currentStep?.showDragHint || this.currentStep?.showScrollArrow;
			return {
				left: "0px",
				top: `${holeBottom}px`,
				width: "100vw",
				height: `${Math.max(0, (window.innerHeight || 0) - holeBottom)}px`,
				pointerEvents: isLowProfile ? "none" : "auto",
				opacity: isLowProfile ? 0.05 : 1
			};
		},
		dragHintStyle() {
			if (!this.targetRect) return { display: "none" };
			// Position to the right of the highlighted integration row
			return {
				left: `${this.targetRect.right + 20}px`,
				top: `${this.targetRect.top + this.targetRect.height / 2}px`,
				transform: "translateY(-50%)"
			};
		},
		cardWidth() {
			return 360;
		},
		cardHeight() {
			const base = this.showManualNext ? 190 : 170;
			return this.currentStep?.codeSnippet ? base + 120 : base;
		},
		cardStyle() {
			const w = this.cardWidth;
			const h = this.cardHeight;
			const margin = 12;
			const viewportW = window.innerWidth || 1200;
			const viewportH = window.innerHeight || 800;

			if (this.manualCardPosition) {
				return {
					left: `${this.manualCardPosition.left}px`,
					top: `${this.manualCardPosition.top}px`,
					width: `${w}px`
				};
			}

			if (!this.targetRect) {
				return {
					left: `${Math.max(margin, (viewportW - w) / 2)}px`,
					top: `${Math.max(margin, (viewportH - h) / 2)}px`,
					width: `${w}px`
				};
			}

			const placement = this.currentStep?.cardPlacement || "auto";
			let left = this.targetRect.left + this.targetRect.width / 2 - w / 2;
			let top = this.targetRect.bottom + margin;

			if (placement === "right") {
				left = this.targetRect.right + margin;
				top = this.targetRect.top;
			} else if (placement === "center-right") {
				left = viewportW - w - margin * 3;
				top = (viewportH - h) / 2;
			} else if (placement === "left") {
				left = this.targetRect.left - w - margin;
				top = this.targetRect.top;
			} else if (placement === "top") {
				top = this.targetRect.top - h - margin;
			} else if (placement === "bottom") {
				top = this.targetRect.bottom + margin;
			} else {
				// auto
				if (top + h > viewportH - margin) top = this.targetRect.top - h - margin;
			}

			left = Math.min(Math.max(margin, left), viewportW - w - margin);
			top = Math.min(Math.max(margin, top), viewportH - h - margin);
			return { left: `${left}px`, top: `${top}px`, width: `${w}px` };
		},
		scrollSuggestionStyle() {
			if (!this.modalRect) return {};
			const padding = 40;
			return {
				left: `${this.modalRect.left + (this.modalRect.width * 0.92)}px`,
				top: `${this.modalRect.top + padding}px`,
				transform: "translateX(-50%)"
			};
		}
	},
	watch: {
		visible(val) {
			if (val) this.open(this.initialStepIndex);
			else this.cleanup();
		},
		stepIndex(newVal, oldVal) {
			this.manualCardPosition = null;
			this.missingTargetTicks = 0;
			this.$nextTick(() => this.recalc(true));

			const direction = newVal > oldVal ? "forward" : "backward";
			this.$emit("step-change", {
				index: newVal,
				direction,
				step: this.steps[newVal]
			});
		}
	},
	mounted() {
		if (this.visible) this.open();
	},
	beforeDestroy() {
		this.cleanup();
	},
	methods: {
		open(startIndex = 0) {
			this.stepIndex = startIndex;
			this.$nextTick(() => this.recalc(true));
			window.addEventListener("resize", this.onWindowChange, { passive: true });
			window.addEventListener("scroll", this.onWindowChange, true);
			document.addEventListener("click", this.onDocumentClick, true);
			document.addEventListener("dblclick", this.onDocumentDblClick, true);
			document.addEventListener("input", this.onDocumentInput, true);
			document.addEventListener("mousemove", this.onDragMove, true);
			document.addEventListener("mouseup", this.onDragEnd, true);
			document.addEventListener("touchmove", this.onDragMove, { passive: false, capture: true });
			document.addEventListener("touchend", this.onDragEnd, true);
			window.addEventListener("workflow-tour-workflow-created", this.onWorkflowCreatedEvent);
			this.autoAdvanceTimer = setInterval(() => this.checkAutoAdvance(), 100);
		},
		cleanup() {
			window.removeEventListener("resize", this.onWindowChange);
			window.removeEventListener("scroll", this.onWindowChange, true);
			document.removeEventListener("click", this.onDocumentClick, true);
			document.removeEventListener("dblclick", this.onDocumentDblClick, true);
			document.removeEventListener("input", this.onDocumentInput, true);
			document.removeEventListener("mousemove", this.onDragMove, true);
			document.removeEventListener("mouseup", this.onDragEnd, true);
			document.removeEventListener("touchmove", this.onDragMove, true);
			document.removeEventListener("touchend", this.onDragEnd, true);
			window.removeEventListener("workflow-tour-workflow-created", this.onWorkflowCreatedEvent);
			if (this.recalcTimer) clearTimeout(this.recalcTimer);
			if (this.autoAdvanceTimer) clearInterval(this.autoAdvanceTimer);
			this.recalcTimer = null;
			this.autoAdvanceTimer = null;
			this.targetRect = null;
			this.advanceInProgress = false;
			this.manualCardPosition = null;
			this.dragging = false;
		},
		onWindowChange() {
			this.recalc(false);
		},
		getPointerXY(e) {
			if (e.touches && e.touches[0]) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
			return { x: e.clientX, y: e.clientY };
		},
		onDragStart(e) {
			const header = e.target && e.target.closest ? e.target.closest(".tour-card__header") : null;
			if (!header) return;
			
			const pointer = this.getPointerXY(e);
			const card = e.currentTarget;
			const rect = card.getBoundingClientRect();
			
			this.dragging = true;
			this.dragOffsetX = pointer.x - rect.left;
			this.dragOffsetY = pointer.y - rect.top;
			this.manualCardPosition = { left: rect.left, top: rect.top };

			window.addEventListener("mousemove", this.onDragMove);
			window.addEventListener("touchmove", this.onDragMove, { passive: false });
			window.addEventListener("mouseup", this.onDragEnd);
			window.addEventListener("touchend", this.onDragEnd);
		},
		onDragMove(e) {
			if (!this.dragging) return;
			if (e.cancelable) e.preventDefault();
			const pointer = this.getPointerXY(e);
			
			const w = this.cardWidth;
			const h = this.cardHeight;
			const margin = 12;
			const viewportW = window.innerWidth || 1200;
			const viewportH = window.innerHeight || 800;

			const left = Math.min(viewportW - w - margin, Math.max(margin, pointer.x - this.dragOffsetX));
			const top = Math.min(viewportH - h - margin, Math.max(margin, pointer.y - this.dragOffsetY));
			
			this.manualCardPosition = { left, top };
		},
		onDragEnd() {
			this.dragging = false;
			window.removeEventListener("mousemove", this.onDragMove);
			window.removeEventListener("touchmove", this.onDragMove);
			window.removeEventListener("mouseup", this.onDragEnd);
			window.removeEventListener("touchend", this.onDragEnd);
		},
		onWorkflowCreatedEvent() {
			// Edge case: user presses Enter on name input and workflow is created
			// before clicking Next. Jump tour to "created workflow row" step.
			if (!this.visible || !this.currentStep) return;
			if (this.currentStep.target !== "#wf-tour-workflow-name-input input") return;
			const rowStepIdx = (this.steps || []).findIndex((s) => s.target === "#wf-tour-created-workflow-row");
			if (rowStepIdx >= 0) {
				this.stepIndex = rowStepIdx;
			} else {
				this.advance();
			}
		},
		getAdvanceRule() {
			return this.currentStep?.advanceWhen || null;
		},
		ruleMatches(rule) {
			if (!rule) return true;
			if (rule.type === "input_non_empty") {
				const el = this.resolveInputElement(rule.selector);
				if (!el) return false;
				return String(el.value || "").trim().length > 0;
			}
			if (rule.type === "input_contains") {
				const el = this.resolveInputElement(rule.selector);
				if (!el) return false;
				const value = String(el.value || "").toLowerCase().trim();
				const expected = String(rule.value || "").toLowerCase().trim();
				return !!expected && value.includes(expected);
			}
			if (rule.type === "element_visible") {
				const el = document.querySelector(rule.selector);
				return !!(el && el.offsetParent !== null);
			}
			if (rule.type === "local_storage_equals") {
				return localStorage.getItem(rule.key) === rule.value;
			}
			if (rule.type === "element_with_text_visible") {
				if (!rule.selector || !rule.text) return false;
				const candidates = Array.from(document.querySelectorAll(rule.selector));
				return candidates.some((el) => String(el.textContent || "").includes(rule.text));
			}
			if (rule.type === "element_not_visible") {
				if (!rule.selector) return true;
				const el = document.querySelector(rule.selector);
				return !el || el.offsetParent === null;
			}
			return true;
		},
		advance() {
			if (this.advanceInProgress) return;
			this.advanceInProgress = true;
			setTimeout(() => {
				if (this.stepIndex >= this.steps.length - 1) {
					this.$emit("completed");
					this.close();
				} else {
					this.stepIndex = Math.min(this.steps.length - 1, this.stepIndex + 1);
				}
				this.advanceInProgress = false;
			}, 80);
		},
		prev() {
			if (this.advanceInProgress) return;
			this.advanceInProgress = true;
			setTimeout(() => {
				this.stepIndex = Math.max(0, this.stepIndex - 1);
				this.advanceInProgress = false;
			}, 80);
		},
		elementMatchesSelector(target, selector) {
			if (!target || !selector) return false;
			if (target.matches && target.matches(selector)) return true;
			return !!(target.closest && target.closest(selector));
		},
		resolveInputElement(selector) {
			if (!selector) return null;

			let el = document.querySelector(selector);
			if (el) {
				if (el.value != null) return el;
				return el.querySelector("input, textarea");
			}

			// Handle selectors like "#some-id input" when id may be on the actual input.
			const baseSelector = selector.replace(/\s+input\s*$/, "").replace(/\s+textarea\s*$/, "");
			if (baseSelector !== selector) {
				el = document.querySelector(baseSelector);
				if (el) {
					if (el.value != null) return el;
					return el.querySelector("input, textarea");
				}
			}

			return null;
		},
		onDocumentClick(e) {
			const rule = this.getAdvanceRule();
			if (!rule) return;

			if (rule.type === "click_target") {
				if (this.elementMatchesSelector(e.target, rule.selector)) {
					if (this.currentStep?.preventTargetDefault) {
						e.preventDefault();
						e.stopPropagation();
					}
					// Advance if not manual, OR if manual but we want to allow click-to-advance
					// In this case, we always advance if the target is specifically clicked
					this.advance();
				}
				return;
			}
			if (rule.type === "click_text_contains") {
				if (this.currentStep?.manualAdvance) return;
				if (!this.elementMatchesSelector(e.target, rule.selector)) return;
				const targetText = String((e.target && e.target.textContent) || "").trim();
				const expected = String(rule.text || "").trim();
				if (expected && targetText.includes(expected)) this.advance();
			}
		},
		onDocumentDblClick(e) {
			if (this.currentStep?.manualAdvance) return;
			const rule = this.getAdvanceRule();
			if (!rule || rule.type !== "dblclick_target") return;
			if (this.elementMatchesSelector(e.target, rule.selector)) this.advance();
		},
		onDocumentInput(e) {
			this.inputChangeTick += 1;
			if (this.currentStep?.manualAdvance) return;
			const rule = this.getAdvanceRule();
			if (!rule) return;
			if (rule.type === "input_non_empty") {
				if (!this.elementMatchesSelector(e.target, rule.selector)) return;
				if (String(e.target.value || "").trim().length > 0) this.advance();
			}
			if (rule.type === "input_contains") {
				if (!this.elementMatchesSelector(e.target, rule.selector)) return;
				const value = String(e.target.value || "").toLowerCase().trim();
				const expected = String(rule.value || "").toLowerCase().trim();
				if (expected && value.includes(expected)) this.advance();
			}
		},
		checkAutoAdvance() {
			// Periodically refresh highlight position to track moving elements (like drawers)
			this.recalc(false);

			if (this.currentStep?.manualAdvance) return;
			const rule = this.getAdvanceRule();
			if (!rule) return;
			if (rule.type === "element_visible") {
				const el = document.querySelector(rule.selector);
				if (el && el.offsetParent !== null) this.advance();
			}
			if (rule.type === "local_storage_equals") {
				if (localStorage.getItem(rule.key) === rule.value) this.advance();
			}
			if (rule.type === "element_with_text_visible") {
				const candidates = Array.from(document.querySelectorAll(rule.selector || ""));
				const expected = String(rule.text || "").trim();
				const found = candidates.some((el) => String(el.textContent || "").includes(expected));
				if (found) this.advance();
			}
		},
		getTargetEl() {
			const selector = this.currentStep?.target;
			if (!selector) return null;
			try {
				const keyText = String(this.currentStep?.targetTextContains || "").trim();
				if (keyText) {
					const all = Array.from(document.querySelectorAll(selector));
					const directMatch = all.find((el) => String(el.textContent || "").includes(keyText));
					if (directMatch) return directMatch;

					const rootSelector = selector.split(" ")[0];
					const root = document.querySelector(rootSelector);
					if (root) {
						const fallback = Array.from(root.querySelectorAll("*")).find((el) =>
							String(el.textContent || "").trim() === keyText
						);
						if (fallback) return fallback;
					}
					return null;
				}

				// Enhanced resolution similar to resolveInputElement
				let el = document.querySelector(selector);
				// If multiple found, try to find the one that is visible or has non-zero size
				if (!el || (el.offsetWidth === 0 && el.offsetHeight === 0)) {
					const all = Array.from(document.querySelectorAll(selector));
					el = all.find(e => e.offsetWidth > 0 || e.offsetHeight > 0) || el;
				}

				if (el) {
					// If it's a wrapper like el-input, we usually want to highlight the actual input area
					if (el.classList.contains("el-input") || el.classList.contains("el-textarea")) {
						const inner = el.querySelector("input, textarea");
						if (inner) return inner;
					}
					return el;
				}

				// Handle cases where the ID is on the input but selector says "#id input"
				if (selector.includes(" input") || selector.includes(" textarea")) {
					const baseSelector = selector.replace(/\s+(input|textarea)\s*$/, "");
					const allBase = Array.from(document.querySelectorAll(baseSelector));
					el = allBase.find(e => (e.tagName === "INPUT" || e.tagName === "TEXTAREA") && e.offsetWidth > 0) || allBase[0];
					if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA")) return el;
				}

				return null;
			} catch (e) {
				return null;
			}
		},
		recalc(forceScroll) {
			const el = this.getTargetEl();
			if (!el) {
				this.missingTargetTicks += 1;
				
				// If target is missing and we should wait, set a retry timer if not already waiting.
				if (this.currentStep?.waitForTarget && !this.recalcTimer) {
					// Increase wait time slightly and allow more retries before clearing targetRect
					this.recalcTimer = setTimeout(() => {
						this.recalcTimer = null;
						this.recalc(forceScroll);
					}, 200);
				}

				if (this.currentStep?.waitForTarget && this.currentStep?.recoverToTitle && this.missingTargetTicks >= 12) {
					const recoverTitle = this.currentStep.recoverToTitle;
					const recoverStepIdx = (this.steps || []).findIndex((s) => s.title === recoverTitle);
					if (recoverStepIdx >= 0 && recoverStepIdx !== this.stepIndex) {
						this.stepIndex = recoverStepIdx;
						this.$message({
							type: "info",
							message: "Returning to the previous tour step.",
							duration: 1200
						});
						return;
					}
				}

				// Key Fix: Don't clear targetRect immediately if we are in a waitForTarget step.
				// This prevents the card from jumping to the center of the screen during transitions.
				// Only clear after significant number of ticks.
				if (!this.currentStep?.waitForTarget || this.missingTargetTicks > 10) {
					this.targetRect = null;
				}
				return;
			}
			
			// Target found, clear any pending retry timers
			if (this.recalcTimer) {
				clearTimeout(this.recalcTimer);
				this.recalcTimer = null;
			}

			this.missingTargetTicks = 0;
			if (this.currentStep?.autoScroll && this.shouldScrollToTarget(el)) {
				try {
					// Use 'nearest' to prevent scrolling the whole page if already partially visible
					el.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
					// Give a moment for scroll to complete before final rect calculation
					if (forceScroll) {
						setTimeout(() => this.recalc(false), 350);
					}
				} catch (e) {
					console.log(e);
				}
			}

			// Update modal rect for positioning scroll hints
			const modalEl = document.querySelector(".nodeContentsModal");
			if (modalEl && modalEl.offsetParent !== null) {
				this.modalRect = modalEl.getBoundingClientRect();
			} else {
				this.modalRect = null;
			}

			if (!el || this.currentStep?.showScrollArrow) {
				this.targetRect = null;
				return;
			}
			const rect = el.getBoundingClientRect();
			this.targetRect = {
				left: rect.left,
				top: rect.top,
				right: rect.right,
				bottom: rect.bottom,
				width: rect.width,
				height: rect.height
			};
		},
		close() {
			this.$emit("update:visible", false);
		},
		end() {
			this.$emit("ended");
			this.close();
		},
		shouldScrollToTarget(el) {
			if (!el || !this.currentStep?.autoScroll) return false;
			const rect = el.getBoundingClientRect();
			const windowHeight = window.innerHeight || document.documentElement.clientHeight;
			const windowWidth = window.innerWidth || document.documentElement.clientWidth;
			
			// If element is not in viewport at all, or very close to edges, we should scroll.
			const isVisible = (
				rect.top >= 120 &&
				rect.left >= 0 &&
				rect.bottom <= windowHeight - 120 &&
				rect.right <= windowWidth
			);

			return !isVisible;
		},
		async copySnippet() {
			const snippet = this.currentStep?.codeSnippet;
			if (!snippet) return;
			try {
				if (navigator?.clipboard?.writeText) {
					await navigator.clipboard.writeText(snippet);
				} else {
					const textarea = document.createElement("textarea");
					textarea.value = snippet;
					document.body.appendChild(textarea);
					textarea.select();
					document.execCommand("copy");
					document.body.removeChild(textarea);
				}
				this.$message.success("Sample JSON copied");
			} catch (e) {
				console.log(e);
				this.$message.error("Unable to copy sample JSON");
			}
		}
	}
};
</script>

<style scoped>
.tour-root {
	position: fixed;
	inset: 0;
	z-index: 5000;
	pointer-events: none;
}

.tour-mask {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.45);
	z-index: 5001;
	pointer-events: auto;
}

.tour-mask-segment {
	position: fixed;
	background: transparent;
	pointer-events: auto;
	z-index: 5001;
}

.tour-highlight {
	position: fixed;
	z-index: 5002;
	border-radius: 8px;
	border: 2px solid rgba(255, 255, 255, 0.85);
	box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.45),
		0 0 0 4px rgba(255, 255, 255, 0.2),
		0 0 24px rgba(255, 255, 255, 0.15);
	animation: tour-highlight-pulse 2s ease-in-out infinite;
	pointer-events: none;
	transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1),
		top 0.3s cubic-bezier(0.4, 0, 0.2, 1),
		width 0.3s cubic-bezier(0.4, 0, 0.2, 1),
		height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tour-card {
	position: fixed;
	z-index: 5003;
	background: #ffffff;
	border-radius: 12px;
	box-shadow: 0 16px 48px rgba(0, 0, 0, 0.35);
	overflow: hidden;
	pointer-events: auto;
	transition: left 0.4s cubic-bezier(0.4, 0, 0.2, 1),
		top 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.tour-card.is-dragging {
	transition: none !important;
}

.tour-card__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 12px 8px;
	border-bottom: 1px solid #eef2f7;
	cursor: move;
}

.tour-card__title {
	font-weight: 700;
	color: #111827;
	font-size: 14px;
}

.tour-card__close {
	border: none;
	background: transparent;
	cursor: pointer;
	font-size: 20px;
	line-height: 20px;
	color: #6b7280;
	padding: 0 4px;
}

.tour-card__close:hover {
	color: #111827;
}

.tour-card__drag-hint {
	font-size: 11px;
	color: #9ca3af;
	margin-left: auto;
	margin-right: 10px;
	user-select: none;
}

.tour-card__body {
	padding: 10px 12px 0px;
}

.tour-card__content {
	font-size: 13px;
	line-height: 1.5;
	color: #374151;
	text-align: left;
	white-space: pre-wrap;
}

.tour-card__snippet {
	margin-top: 10px;
	background: #f8fafc;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
}

.tour-card__snippet pre {
	margin: 0;
	padding: 10px;
	font-size: 12px;
	line-height: 1.45;
	color: #1f2937;
	white-space: pre-wrap;
	word-break: break-word;
}

.tour-card__snippet-actions {
	display: flex;
	justify-content: flex-end;
	padding: 0 10px 10px;
}

.tour-card__footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	padding: 10px 12px 12px;
}

.tour-card__progress {
	font-size: 12px;
	color: #6b7280;
}

.tour-card__actions {
	display: inline-flex;
	gap: 8px;
}

@keyframes tour-highlight-pulse {
	0% {
		box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.45),
			0 0 0 3px rgba(255, 255, 255, 0.15),
			0 0 15px rgba(255, 255, 255, 0.1);
	}
	50% {
		box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.45),
			0 0 0 6px rgba(255, 255, 255, 0.35),
			0 0 30px rgba(255, 255, 255, 0.25);
	}
	100% {
		box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.45),
			0 0 0 3px rgba(255, 255, 255, 0.15),
			0 0 15px rgba(255, 255, 255, 0.1);
	}
}

/* ── Connection-arrow overlay ── */

.tour-connect-overlay {
	position: fixed;
	bottom: 20px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 5003;
	pointer-events: none;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 18px;
	animation: tour-overlay-fade-in 0.5s ease;
}

.tour-connect-svg {
	width: min(580px, 80vw);
	height: auto;
	filter: drop-shadow(0 4px 24px rgba(59, 130, 246, 0.18));
}

.tour-node-box {
	fill: #1e293b;
	stroke: #3b82f6;
	stroke-width: 2;
	filter: drop-shadow(0 2px 8px rgba(59, 130, 246, 0.25));
}

.tour-node-label {
	fill: #e2e8f0;
	font-size: 14px;
	font-weight: 600;
	font-family: 'Inter', 'Segoe UI', sans-serif;
}

.tour-arrow-line {
	stroke: #3b82f6;
	stroke-width: 2.5;
	stroke-dasharray: 8 4;
	animation: tour-dash-flow 0.8s linear infinite;
}

.tour-arrow-head {
	fill: #3b82f6;
	animation: tour-arrow-head-pulse 1.4s ease-in-out infinite;
}

.tour-arrow-dot {
	fill: #60a5fa;
	filter: drop-shadow(0 0 6px rgba(96, 165, 250, 0.7));
}

.tour-arrow-dot--delayed {
	animation-delay: 0.7s;
}

.tour-connect-hint {
	background: rgba(30, 41, 59, 0.92);
	color: #e2e8f0;
	padding: 10px 22px;
	border-radius: 10px;
	font-size: 14px;
	font-weight: 500;
	letter-spacing: 0.2px;
	line-height: 1.5;
	text-align: center;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
	max-width: 520px;
}

.tour-connect-hint__icon {
	display: inline-block;
	margin-right: 6px;
	font-size: 18px;
	animation: tour-hint-arrow-bounce 1.2s ease-in-out infinite;
}

@keyframes tour-overlay-fade-in {
	from { opacity: 0; transform: translate(-50%, -48%); }
	to   { opacity: 1; transform: translate(-50%, -50%); }
}

@keyframes tour-dash-flow {
	to { stroke-dashoffset: -24; }
}

@keyframes tour-arrow-head-pulse {
	0%, 100% { opacity: 0.7; }
	50%      { opacity: 1; }
}

@keyframes tour-hint-arrow-bounce {
	0%, 100% { transform: translateX(0); }
	50%      { transform: translateX(6px); }
}

/* ── Drag-hint overlay ── */

.tour-drag-hint-overlay {
	position: fixed;
	z-index: 5004;
	pointer-events: none;
	animation: tour-drag-hint-fade-in 0.4s ease;
}

.tour-drag-hint-container {
	display: flex;
	align-items: center;
	gap: 12px;
	background: #ffffff;
	padding: 8px 16px;
	border-radius: 12px;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.08);
	border: 1px solid rgba(0, 0, 0, 0.05);
}

.tour-drag-hint-arrow {
	display: flex;
	align-items: center;
}

.tour-drag-line {
	animation: tour-dash-flow 0.8s linear infinite;
}

.tour-drag-arrow-head {
	animation: tour-arrow-head-pulse 1s ease-in-out infinite;
}

.tour-drag-dot {
	filter: drop-shadow(0 0 4px rgba(96, 165, 250, 0.6));
}

.tour-drag-hint-label {
	background: #210f3c; /* Matches your brand color */
	color: #ffffff;
	padding: 6px 14px;
	border-radius: 8px;
	font-size: 13px;
	font-weight: 600;
	letter-spacing: 0.3px;
	white-space: nowrap;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
	animation: tour-hint-arrow-bounce 1.2s ease-in-out infinite;
}

@keyframes tour-drag-hint-fade-in {
	from { opacity: 0; transform: translateY(-40%); }
	to   { opacity: 1; transform: translateY(-50%); }
}

/* ── Scroll suggestion ── */

.tour-scroll-suggestion {
	position: fixed;
	z-index: 5005;
	pointer-events: none;
	animation: tour-fade-in 0.4s ease;
}

.tour-scroll-suggestion.is-down {
	/* Standard fallback if no modalRect */
	bottom: 120px;
}

.tour-scroll-suggestion.is-up {
	/* Standard fallback if no modalRect */
	top: 120px;
}

.tour-scroll-suggestion__content {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	padding: 10px 18px;
	background: rgba(30, 41, 59, 0.9);
	backdrop-filter: blur(8px);
	border-radius: 100px;
	border: 1px solid rgba(255, 255, 255, 0.15);
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.is-up .tour-scroll-suggestion__content {
	flex-direction: column-reverse;
}

.tour-scroll-suggestion__arrow {
	color: #60a5fa;
	filter: drop-shadow(0 0 4px rgba(96, 165, 250, 0.4));
}

.tour-scroll-suggestion__arrow svg {
	width: 28px;
	height: 28px;
}

.is-down .tour-scroll-suggestion__arrow {
	animation: tour-scroll-bounce-down 1.4s ease-in-out infinite;
}

.is-up .tour-scroll-suggestion__arrow {
	animation: tour-scroll-bounce-up 1.4s ease-in-out infinite;
}

.tour-scroll-suggestion__text {
	color: #fff;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.8px;
	text-transform: uppercase;
}

@keyframes tour-scroll-bounce-down {
	0%, 100% { transform: translateY(0); }
	50%      { transform: translateY(8px); }
}

@keyframes tour-scroll-bounce-up {
	0%, 100% { transform: translateY(0); }
	50%      { transform: translateY(-8px); }
}

@keyframes tour-fade-in {
	from { opacity: 0; }
	to   { opacity: 1; }
}
</style>
